import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { ApisService } from '@core/services/apis.service';
import { LanguageService } from '@core/services/language.service';
import { LoadingService } from '@core/services/loading.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ColumnMode, DatatableComponent } from '@swimlane/ngx-datatable';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-contests',
  templateUrl: './contests.component.html',
  styleUrls: ['./contests.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ContestsComponent implements OnInit {
  @ViewChild(DatatableComponent) table: DatatableComponent;

  public contests: any[] = [];
  public ColumnMode = ColumnMode;
  public itemsPerPage = 10;
  public lang: any;

  public isEdit = false;
  public contestEntries: any[] = [];
  public selectedContestTitle = '';
  public entriesQuery = '';
  public form: any = {
    contest_id: null,
    title: '',
    description: '',
    rules: '',
    prizes: '',
    status: 'draft',
    is_active: true,
    submit_start_at: '',
    submit_end_at: '',
    vote_start_at: '',
    vote_end_at: ''
  };

  public statuses = [
    'draft',
    'upcoming',
    'submission_open',
    'voting_open',
    'closed'
  ];

  constructor(
    private apisService: ApisService,
    private loading: LoadingService,
    private toastr: ToastrService,
    private modal: NgbModal,
    private language: LanguageService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.language.currentLang.subscribe(data => (this.lang = data));
    this.getContests();
  }

  getContests() {
    this.loading.show();
    this.apisService.getDashboardContests().subscribe({
      next: (res: any) => {
        this.loading.hide();
        this.contests = res?.data || [];
      },
      error: () => {
        this.loading.hide();
        this.toastr.error(this.lang === 'ar' ? 'حدث خطأ أثناء تحميل المسابقات' : 'Failed to load contests');
      }
    });
  }

  openModal(content: any, row?: any) {
    this.isEdit = !!row;
    if (row) {
      this.form = {
        contest_id: row.id,
        title: row.title || '',
        description: row.description || '',
        rules: row.rules || '',
        prizes: row.prizes || '',
        status: row.status || 'draft',
        is_active: row.is_active === true || row.is_active === 1,
        submit_start_at: this.toInputDate(row.submit_start_at),
        submit_end_at: this.toInputDate(row.submit_end_at),
        vote_start_at: this.toInputDate(row.vote_start_at),
        vote_end_at: this.toInputDate(row.vote_end_at)
      };
    } else {
      this.resetForm();
    }

    this.modal.open(content, { centered: true, size: 'lg' });
  }

  saveContest() {
    const payload = {
      ...this.form,
      is_active: !!this.form.is_active
    };

    if (this.isEdit) {
      this.apisService.updateDashboardContest(payload).subscribe({
        next: () => {
          this.toastr.success(this.lang === 'ar' ? 'تم تعديل المسابقة بنجاح' : 'Contest updated successfully');
          this.modal.dismissAll();
          this.getContests();
        },
        error: () => this.toastr.error(this.lang === 'ar' ? 'فشل تعديل المسابقة' : 'Failed to update contest')
      });
    } else {
      this.apisService.createDashboardContest(payload).subscribe({
        next: () => {
          this.toastr.success(this.lang === 'ar' ? 'تم إضافة المسابقة بنجاح' : 'Contest created successfully');
          this.modal.dismissAll();
          this.getContests();
        },
        error: () => this.toastr.error(this.lang === 'ar' ? 'فشل إضافة المسابقة' : 'Failed to create contest')
      });
    }
  }

  deleteContest(id: number) {
    if (!confirm(this.lang === 'ar' ? 'هل تريد حذف المسابقة؟' : 'Delete this contest?')) return;

    this.apisService.deleteDashboardContest(id).subscribe({
      next: () => {
        this.toastr.success(this.lang === 'ar' ? 'تم حذف المسابقة' : 'Contest deleted');
        this.getContests();
      },
      error: () => this.toastr.error(this.lang === 'ar' ? 'فشل حذف المسابقة' : 'Failed to delete contest')
    });
  }

  openEntriesModal(content: any, row: any) {
    this.selectedContestTitle = row?.title || '';
    this.form.contest_id = row?.id;
    this.entriesQuery = '';
    this.contestEntries = [];
    this.loading.show();
    this.apisService.getDashboardContestEntries(row.id).subscribe({
      next: (res: any) => {
        this.loading.hide();
        this.contestEntries = res?.data || [];
        this.modal.open(content, { centered: true, size: 'xl' });
      },
      error: () => {
        this.loading.hide();
        this.toastr.error(this.lang === 'ar' ? 'فشل تحميل المشاركات' : 'Failed to load entries');
      }
    });
  }

  setWinner(entry: any) {
    const contestId = this.form?.contest_id;
    if (!contestId || !entry?.id) return;

    if (!confirm(this.lang === 'ar' ? 'تأكيد اختيار هذه المشاركة كفائز؟' : 'Set this entry as the winner?')) return;

    this.loading.show();
    this.apisService.setDashboardContestWinner(contestId, entry.id, null).subscribe({
      next: () => {
        this.loading.hide();
        this.toastr.success(this.lang === 'ar' ? 'تم تحديد الفائز' : 'Winner selected');
        // reload entries to update is_winner
        this.apisService.getDashboardContestEntries(contestId).subscribe({
          next: (res: any) => {
            this.contestEntries = res?.data || [];
          },
          error: () => {},
        });
      },
      error: () => {
        this.loading.hide();
        this.toastr.error(this.lang === 'ar' ? 'فشل تحديد الفائز' : 'Failed to set winner');
      }
    });
  }

  clearWinner() {
    const contestId = this.form?.contest_id;
    if (!contestId) return;

    if (!confirm(this.lang === 'ar' ? 'مسح الفائز الحالي؟' : 'Clear the current winner?')) return;

    this.loading.show();
    this.apisService.clearDashboardContestWinner(contestId).subscribe({
      next: () => {
        this.loading.hide();
        this.toastr.success(this.lang === 'ar' ? 'تم مسح الفائز' : 'Winner cleared');
        this.apisService.getDashboardContestEntries(contestId).subscribe({
          next: (res: any) => {
            this.contestEntries = res?.data || [];
          },
          error: () => {},
        });
      },
      error: () => {
        this.loading.hide();
        this.toastr.error(this.lang === 'ar' ? 'فشل مسح الفائز' : 'Failed to clear winner');
      }
    });
  }

  filteredEntries(): any[] {
    const q = (this.entriesQuery || '').trim().toLowerCase();
    if (!q) return this.contestEntries || [];

    return (this.contestEntries || []).filter((e: any) => {
      const story = String(e?.story_title || '').toLowerCase();
      const writer = String(e?.writer_name || '').toLowerCase();
      const id = String(e?.id || '');
      return story.includes(q) || writer.includes(q) || id.includes(q);
    });
  }

  openEntryReview(entry: any) {
    if (!entry?.id) return;
    this.modal.dismissAll();
    this.router.navigate(['/contests/entries', entry.id]);
  }

  getStatusLabel(status: string): string {
    const key = (status || 'draft').toLowerCase();
    const mapAr: any = {
      draft: 'مسودة',
      upcoming: 'قادمة',
      submission_open: 'التقديم مفتوح',
      voting_open: 'التصويت مفتوح',
      closed: 'مغلقة'
    };
    const mapEn: any = {
      draft: 'Draft',
      upcoming: 'Upcoming',
      submission_open: 'Submission Open',
      voting_open: 'Voting Open',
      closed: 'Closed'
    };
    return this.lang === 'ar' ? (mapAr[key] || key) : (mapEn[key] || key);
  }

  close() {
    this.modal.dismissAll();
  }

  private resetForm() {
    this.form = {
      contest_id: null,
      title: '',
      description: '',
      rules: '',
      prizes: '',
      status: 'draft',
      is_active: true,
      submit_start_at: '',
      submit_end_at: '',
      vote_start_at: '',
      vote_end_at: ''
    };
  }

  private toInputDate(value: string) {
    if (!value) return '';
    // supports 'YYYY-MM-DD HH:mm:ss' and ISO
    return value.replace(' ', 'T').slice(0, 16);
  }
}
